import React from "react";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import {
  BATCH_REPORT_GENERATE_STATUS,
  BATCH_REPORT_TEST_STATUS,
} from "../../../../utils/statusConfig";
import { buildDataOpsExportFileName, downloadDataOpsExcel } from "./utils";

export const OVERDUE_PHASE_KEYS = [
  "physical_sample_preparation",
  "analytical_results_phase",
];

export const OVERDUE_PHASE_LABELS = {
  physical_sample_preparation: "Physical sample preparation",
  analytical_results_phase: "Analytical results",
};

export const EMPTY_CELL_VALUE = "-";

/**
 * Data Ops–only result statuses not present in BATCH_REPORT_TEST_STATUS.
 * Shared statuses (Pending, Ready, etc.) use BATCH_REPORT_TEST_STATUS colors.
 */
const DATA_OPS_ONLY_RESULT_STATUS = {
  Hold: { label: "Hold", color: "#ff0000" },
  TNTC: { label: "TNTC", color: "#FF9F3F" },
};

/**
 * Result status labels + colors for Data Ops drill-down modals / Excel.
 * Shared keys mirror BATCH_REPORT_TEST_STATUS; Hold / TNTC are Data Ops–only.
 */
export const DATA_OPS_RESULT_STATUS = {
  Pending: {
    label: BATCH_REPORT_TEST_STATUS.Pending.label,
    color: BATCH_REPORT_TEST_STATUS.Pending.style.color,
  },
  Retest: {
    label: BATCH_REPORT_TEST_STATUS.Retest.label,
    color: BATCH_REPORT_TEST_STATUS.Retest.style.color,
  },
  Ready: {
    label: BATCH_REPORT_TEST_STATUS.Ready.label,
    color: BATCH_REPORT_TEST_STATUS.Ready.style.color,
  },
  "Ready-P": {
    label: BATCH_REPORT_TEST_STATUS["Ready-P"].label,
    color: BATCH_REPORT_TEST_STATUS["Ready-P"].style.color,
  },
  Complete: {
    label: BATCH_REPORT_TEST_STATUS.Complete.label,
    color: BATCH_REPORT_TEST_STATUS.Complete.style.color,
  },
  ...DATA_OPS_ONLY_RESULT_STATUS,
};

/**
 * Build Lab Ops API date_range (MM/DD/YYYY-MM/DD/YYYY) from a chart period.
 * Used by SLA / reason-period drill-downs (period start/end from chart row bounds).
 * @param {*} periodStart
 * @param {*} periodEnd
 * @returns {string|null}
 */
export const buildLabOpsDateRangeFromPeriod = (periodStart, periodEnd) => {
  const startDate = dayjs(periodStart);
  const endDate = dayjs(periodEnd || periodStart);
  if (!startDate.isValid() || !endDate.isValid()) {
    return null;
  }
  return `${startDate.format("MM/DD/YYYY")}-${endDate.format("MM/DD/YYYY")}`;
};

/**
 * Flatten overdue_by_phase result buckets into table rows, preserving phase.
 * @param {Record<string, object[]>} [result]
 * @returns {object[]}
 */
export const flattenOverdueByPhaseResult = (result = {}) => {
  const rows = [];

  OVERDUE_PHASE_KEYS.forEach((phaseKey) => {
    const phaseRows = Array.isArray(result[phaseKey]) ? result[phaseKey] : [];
    phaseRows.forEach((row, index) => {
      rows.push({
        ...row,
        phase: phaseKey,
        rowKey: [
          phaseKey,
          row.workorder_id,
          row.genista_id,
          row.test_id,
          index,
        ].join("-"),
      });
    });
  });

  return rows;
};

/**
 * Display helper for optional API fields (status / days_overdue may be missing).
 * @param {*} value
 * @returns {string|number}
 */
export const formatOverdueCellValue = (value) => {
  if (value == null || value === "") {
    return EMPTY_CELL_VALUE;
  }
  return value;
};

/** Overdue export `has_rca` display values from the API. */
export const HAS_RCA_STATUS = {
  NO_RCA: "No RCA",
  COMPLETED_RCA: "Completed RCA",
  ACTIVE_RCA: "Active RCA",
};

const HAS_RCA_STATUS_BY_NORMALIZED = {
  "no rca": HAS_RCA_STATUS.NO_RCA,
  "completed rca": HAS_RCA_STATUS.COMPLETED_RCA,
  "complted rca": HAS_RCA_STATUS.COMPLETED_RCA,
  "active rca": HAS_RCA_STATUS.ACTIVE_RCA,
};

/**
 * Normalize overdue `has_rca` to No RCA / Completed RCA / Active RCA.
 * @param {*} value
 * @returns {string|null} null when empty
 */
export const resolveHasRcaLabel = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  return HAS_RCA_STATUS_BY_NORMALIZED[normalized] || String(value).trim();
};

/**
 * Modal cell for Has RCA.
 * @param {*} value
 * @returns {string}
 */
export const formatHasRcaCellValue = (value) => (
  resolveHasRcaLabel(value) ?? EMPTY_CELL_VALUE
);

/**
 * Excel cell for Has RCA — blank when missing.
 * @param {*} value
 * @returns {string}
 */
export const formatHasRcaExportValue = (value) => (
  resolveHasRcaLabel(value) || ""
);

/**
 * Map result_status via BATCH_REPORT_TEST_STATUS / DATA_OPS_RESULT_STATUS (Excel).
 * Empty when unknown — Excel stays blank.
 */
export const formatResultStatusLabel = (resultStatus) => {
  if (resultStatus == null || resultStatus === "") {
    return "";
  }
  const key = String(resultStatus);
  const batchStatus = BATCH_REPORT_TEST_STATUS[key];
  if (batchStatus) {
    return batchStatus.label;
  }
  const dataOpsStatus = DATA_OPS_RESULT_STATUS[key];
  if (!dataOpsStatus) {
    return "";
  }
  return dataOpsStatus.label;
};

/**
 * Map numeric report_status to BATCH_REPORT_GENERATE_STATUS label (Excel).
 * Empty when unknown — Excel stays blank.
 */
export const formatReportStatusLabel = (reportStatus) => {
  if (reportStatus == null || reportStatus === "") {
    return "";
  }
  const statusObj = BATCH_REPORT_GENERATE_STATUS[String(reportStatus)];
  if (!statusObj) {
    return "";
  }
  return statusObj.label;
};

/**
 * Ant Design column ellipsis config — disables native title so Tooltip can own hover text.
 */
export const OVERDUE_ELLIPSIS_COLUMN = {
  showTitle: false,
};

/**
 * Coerce filter values to integer PKs. Empty / invalid values are omitted by the serializer.
 * @param {*} value
 * @returns {number|""}
 */
export const toOptionalIntegerId = (value) => {
  if (value == null || value === "") {
    return "";
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return "";
  }
  return parsed;
};

/**
 * Coerce single or multi-select filter values to integer PK arrays.
 * Empty / invalid entries are dropped; empty arrays are omitted by the serializer.
 * @param {*} value
 * @returns {number[]}
 */
export const toOptionalIntegerIds = (value) => {
  let values;
  if (Array.isArray(value)) {
    values = value;
  } else if (value == null || value === "") {
    values = [];
  } else {
    values = [value];
  }

  return values
    .map((item) => toOptionalIntegerId(item))
    .filter((item) => item !== "");
};

/**
 * Parse `id:123` chart series keys into an integer PK.
 * @param {*} seriesKey
 * @returns {number|""}
 */
export const parseChartSeriesNumericId = (seriesKey) => {
  if (seriesKey == null || seriesKey === "") {
    return "";
  }
  const keyText = String(seriesKey);
  if (!keyText.startsWith("id:")) {
    return "";
  }
  return toOptionalIntegerId(keyText.slice(3));
};

/** Aggregate options for overdue_sla — BE accepts day | week | month only. */
export const SLA_AGGREGATE_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

export const DEFAULT_SLA_AGGREGATE = "day";

/** Recharts dataKey for period-level SLA (vs per-category keys). */
export const SLA_OVERALL_DATA_KEY = "overall";

/**
 * Convert API SLA ratio (0–1) to a chart percentage. Missing / invalid → 0.
 * @param {*} slaRatio
 * @returns {number}
 */
const toSlaPercentage = (slaRatio) => {
  const parsed = Number(slaRatio);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed * 100;
};

/**
 * Build overdue_by_phase query object from filter UI state.
 * Does not include rca (locked out of Overdue tab).
 * Integer PK fields are arrays (multi-select → repeated query params).
 * @param {object} filters
 * @param {Function} formatDateRange — typically formatAntdDateRange
 * @returns {object|null} null when date range is invalid
 */
export const buildOverdueByPhaseParams = (filters, formatDateRange) => {
  const dateRange = formatDateRange(filters.dateRange);
  if (!dateRange) {
    return null;
  }

  return {
    date_range: dateRange,
    test_category_id: toOptionalIntegerIds(filters.testCategoryId),
    test_id: toOptionalIntegerIds(filters.testId),
    customer_pk: toOptionalIntegerId(filters.customerId),
    search: filters.search || "",
    reason_id: toOptionalIntegerId(filters.reasonId),
    retest: Boolean(filters.retest),
    subscription_only: Boolean(filters.subscriptionOnly),
  };
};

/**
 * Build overdue_sla query object — same Overdue filters plus required aggregate.
 * Does not include rca (locked out of Overdue tab).
 * @param {object} filters
 * @param {string} aggregate — day | week | month
 * @param {Function} formatDateRange — typically formatAntdDateRange
 * @returns {object|null} null when date range is invalid
 */
export const buildOverdueSlaParams = (filters, aggregate, formatDateRange) => {
  const baseParams = buildOverdueByPhaseParams(filters, formatDateRange);
  if (!baseParams) {
    return null;
  }

  return {
    ...baseParams,
    aggregate,
  };
};

/**
 * Flatten category_list payload into Select options for tests.
 * When selectedCategoryIds is non-empty, only tests from those categories are included.
 * @param {object[]} categories
 * @param {number[]} selectedCategoryIds
 * @returns {{ value: number, label: string }[]}
 */
export const buildTestOptionsFromCategories = (categories = [], selectedCategoryIds = []) => {
  const options = [];
  const selectedIds = selectedCategoryIds.length > 0
    ? new Set(selectedCategoryIds)
    : null;

  categories.forEach((category) => {
    if (selectedIds && !selectedIds.has(category.id)) {
      return;
    }
    category.tests.forEach((test) => {
      options.push({
        value: test.id,
        label: test.internal_name,
      });
    });
  });

  return options;
};

const UNKNOWN_CHART_LABEL = "Unknown";

/**
 * Normalize a chart axis / legend label from an optional API string.
 * @param {*} value
 * @returns {string}
 */
const toChartLabel = (value) => {
  if (value == null || String(value).trim() === "") {
    return UNKNOWN_CHART_LABEL;
  }
  return String(value).trim();
};

/**
 * Stable bucket key: prefer numeric/string id, fall back to label.
 * @param {*} idValue
 * @param {string} label
 * @returns {string}
 */
const buildChartSeriesKey = (idValue, label) => {
  if (idValue != null && idValue !== "") {
    return `id:${String(idValue)}`;
  }
  return `label:${label}`;
};

/**
 * Map dropdown options ({ value, label }) → Map<id, catalogLabel>.
 * @param {{ value: number|string, label: string }[]} [options]
 * @returns {Map<number, string>}
 */
const buildLabelByIdFromOptions = (options = []) => {
  const labelById = new Map();
  const safeOptions = Array.isArray(options) ? options : [];

  safeOptions.forEach((option) => {
    const optionId = toOptionalIntegerId(option?.value);
    if (optionId === "") {
      return;
    }
    const catalogLabel = String(option?.label ?? "").trim();
    labelById.set(optionId, catalogLabel || String(optionId));
  });

  return labelById;
};

/**
 * Map reason dropdown options ({ value, label }) → Map<reasonId, catalogLabel>.
 * Same IDs / labels as the Overdue reason filter Select.
 * @param {{ value: number|string, label: string }[]} [reasonOptions]
 * @returns {Map<number, string>}
 */
export const buildReasonLabelById = (reasonOptions = []) => (
  buildLabelByIdFromOptions(reasonOptions)
);

/**
 * Map test-category dropdown options → Map<testCategoryId, categoryName>.
 * Same IDs / labels as the Data Ops category filter Select.
 * @param {{ value: number|string, label: string }[]} [categoryOptions]
 * @returns {Map<number, string>}
 */
export const buildCategoryLabelById = (categoryOptions = []) => (
  buildLabelByIdFromOptions(categoryOptions)
);

/**
 * Resolve a test category display name from id + filter catalog map.
 * @param {*} testCategoryId
 * @param {Map<number, string>|null} [categoryLabelById]
 * @returns {string}
 */
export const resolveTestCategoryName = (testCategoryId, categoryLabelById = null) => {
  const categoryId = toOptionalIntegerId(testCategoryId);
  if (categoryId !== "" && categoryLabelById?.has(categoryId)) {
    return categoryLabelById.get(categoryId);
  }
  if (testCategoryId == null || testCategoryId === "") {
    return "";
  }
  return String(testCategoryId);
};

/**
 * Display label for an overdue row's reason — prefer /reasons/ catalog by reason_id.
 * @param {object} row
 * @param {Map<number, string>|null} [reasonLabelById]
 * @returns {string|number}
 */
export const resolveOverdueReasonDisplayLabel = (row = {}, reasonLabelById = null) => {
  const reasonId = toOptionalIntegerId(row.reason_id);
  if (reasonId !== "" && reasonLabelById?.has(reasonId)) {
    return reasonLabelById.get(reasonId);
  }
  return formatOverdueCellValue(row.reason);
};

/**
 * Sort count buckets by count (desc), then name.
 * @param {{ name: string, count: number }[]} buckets
 * @returns {{ name: string, count: number }[]}
 */
const sortCountBuckets = (buckets) => (
  [...buckets].sort((left, right) => {
    if (right.count !== left.count) {
      return right.count - left.count;
    }
    return left.name.localeCompare(right.name);
  })
);

/**
 * Count rows into a primary series with nested secondary counts for tooltips.
 *
 * @param {object[]} rows
 * @param {(row: object) => { key: string, name: string, reasonId?: number|null }} resolvePrimaryBucket
 * @param {(row: object) => { key: string, name: string }} resolveNestedBucket
 * @param {string} nestedSeriesField — e.g. "reasonBreakdown" | "categoryBreakdown"
 * @returns {object[]}
 */
const buildNestedCountChartSeries = (
  rows,
  resolvePrimaryBucket,
  resolveNestedBucket,
  nestedSeriesField,
) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const bucketByKey = new Map();

  safeRows.forEach((row) => {
    const primary = resolvePrimaryBucket(row);
    const nested = resolveNestedBucket(row);

    if (!bucketByKey.has(primary.key)) {
      bucketByKey.set(primary.key, {
        key: primary.key,
        name: primary.name,
        reasonId: primary.reasonId ?? null,
        testCategoryId: primary.testCategoryId ?? null,
        count: 0,
        nestedByKey: new Map(),
      });
    }

    const primaryBucket = bucketByKey.get(primary.key);
    primaryBucket.count += 1;

    if (!primaryBucket.nestedByKey.has(nested.key)) {
      primaryBucket.nestedByKey.set(nested.key, {
        name: nested.name,
        count: 0,
      });
    }
    primaryBucket.nestedByKey.get(nested.key).count += 1;
  });

  return Array.from(bucketByKey.values())
    .map((bucket) => {
      const { nestedByKey, ...seriesPoint } = bucket;
      return {
        ...seriesPoint,
        [nestedSeriesField]: sortCountBuckets(Array.from(nestedByKey.values())),
      };
    })
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      return left.name.localeCompare(right.name);
    });
};

const resolveCategoryBucket = (row) => {
  const name = toChartLabel(row.category_name);
  const testCategoryId = toOptionalIntegerId(row.test_category_id);
  const hasCategoryId = testCategoryId !== "";
  return {
    key: buildChartSeriesKey(hasCategoryId ? testCategoryId : null, name),
    name,
    testCategoryId: hasCategoryId ? testCategoryId : null,
  };
};

/**
 * Reason bucket: key = reason_id (same PK as the reason dropdown).
 * Label = catalog name for that id when available; else row.reason text.
 * @param {object} row
 * @param {Map<number, string>|null} [reasonLabelById]
 * @returns {{ key: string, name: string, reasonId: number|null }}
 */
export const resolveReasonBucket = (row = {}, reasonLabelById = null) => {
  const reasonId = toOptionalIntegerId(row.reason_id);
  const hasReasonId = reasonId !== "";
  const catalogLabel = hasReasonId && reasonLabelById?.has(reasonId)
    ? reasonLabelById.get(reasonId)
    : null;
  const name = catalogLabel != null
    ? toChartLabel(catalogLabel)
    : toChartLabel(row.reason);

  return {
    key: buildChartSeriesKey(hasReasonId ? reasonId : null, name),
    name,
    reasonId: hasReasonId ? reasonId : null,
  };
};

/**
 * Category donut series from flattened overdue rows (same source as the table).
 * Includes reasonBreakdown for tooltip detail (count per reason within the category).
 * @param {object[]} rows
 * @param {Map<number, string>|null} [reasonLabelById]
 * @returns {object[]}
 */
export const buildOverdueCategoryChartSeries = (rows = [], reasonLabelById = null) => (
  buildNestedCountChartSeries(
    rows,
    resolveCategoryBucket,
    (row) => resolveReasonBucket(row, reasonLabelById),
    "reasonBreakdown",
  )
);

/** Max named pie slices before remaining categories roll into "Other". */
export const MAX_NAMED_DONUT_SLICES = 8;

export const OTHER_CHART_SLICE_KEY = "other";
export const OTHER_CHART_SLICE_NAME = "Other";

/**
 * Keep the top named slices for the donut; roll the rest into "Other".
 * Avoids 100-category legend/label clutter while keeping full detail in tooltip.
 *
 * @param {object[]} series — from buildOverdueCategoryChartSeries (count desc)
 * @param {number} [maxNamedSlices]
 * @returns {object[]}
 */
export const collapseOverdueDonutSeries = (
  series = [],
  maxNamedSlices = MAX_NAMED_DONUT_SLICES,
) => {
  const safeSeries = Array.isArray(series) ? series : [];
  if (safeSeries.length === 0) {
    return [];
  }

  if (safeSeries.length <= maxNamedSlices) {
    return safeSeries.map((point) => ({
      ...point,
      showLabel: true,
    }));
  }

  const namedSliceCount = Math.max(1, maxNamedSlices - 1);
  const namedSlices = safeSeries.slice(0, namedSliceCount);
  const rolledSlices = safeSeries.slice(namedSliceCount);
  const otherCount = rolledSlices.reduce((sum, point) => sum + point.count, 0);

  return [
    ...namedSlices.map((point) => ({
      ...point,
      showLabel: true,
    })),
    {
      key: OTHER_CHART_SLICE_KEY,
      name: OTHER_CHART_SLICE_NAME,
      count: otherCount,
      showLabel: true,
      reasonBreakdown: [],
      otherBreakdown: rolledSlices.map((point) => ({
        key: point.key,
        name: point.name,
        count: point.count,
      })),
    },
  ];
};

/** Chart / SLA drill-down sources opened from OverdueCharts. */
export const OVERDUE_DRILL_DOWN_KIND = {
  CATEGORY: "category",
  REASON: "reason",
  SLA: "sla",
};

/**
 * Build overdue_by_phase scoped params for a chart drill-down selection.
 * Returns null when the selection cannot be scoped (e.g. Other / missing id).
 */
export const buildOverdueDrillDownExportParams = (filters, selection, formatDateRange) => {
  const baseParams = buildOverdueByPhaseParams(filters, formatDateRange);
  if (!baseParams || !selection) {
    return null;
  }

  if (selection.kind === OVERDUE_DRILL_DOWN_KIND.CATEGORY) {
    const testCategoryId = toOptionalIntegerId(selection.testCategoryId)
      || parseChartSeriesNumericId(selection.seriesKey);
    if (testCategoryId === "") {
      return null;
    }
    return { ...baseParams, test_category_id: [testCategoryId] };
  }

  if (selection.kind === OVERDUE_DRILL_DOWN_KIND.REASON) {
    const reasonId = toOptionalIntegerId(selection.reasonId)
      || parseChartSeriesNumericId(selection.seriesKey);
    if (reasonId === "") {
      return null;
    }
    return { ...baseParams, reason_id: reasonId };
  }

  if (selection.kind === OVERDUE_DRILL_DOWN_KIND.SLA) {
    const periodDateRange = buildLabOpsDateRangeFromPeriod(
      selection.periodStart,
      selection.periodEnd,
    );
    if (!periodDateRange) {
      return null;
    }

    if (
      selection.categoryKey
      && selection.categoryKey !== SLA_OVERALL_DATA_KEY
    ) {
      const testCategoryId = parseChartSeriesNumericId(selection.categoryKey);
      if (testCategoryId === "") {
        return null;
      }
      return {
        ...baseParams,
        date_range: periodDateRange,
        test_category_id: [testCategoryId],
      };
    }

    return { ...baseParams, date_range: periodDateRange };
  }

  return null;
};

/**
 * Build overdue_by_phase scoped params for a table row click.
 */
export const buildOverdueRowExportParams = (filters, record, formatDateRange) => {
  const baseParams = buildOverdueByPhaseParams(filters, formatDateRange);
  const testId = toOptionalIntegerId(record?.test_id);
  if (!baseParams || testId === "" || !record?.workorder_id || !record?.genista_id) {
    return null;
  }

  return {
    ...baseParams,
    test_id: [testId],
    workorder_id: String(record.workorder_id),
    genista_id: String(record.genista_id),
  };
};

/**
 * Overdue-reason bar series from flattened overdue rows (same source as the table).
 * Buckets by reason_id; bar label uses /reasons/ catalog when provided.
 * Includes categoryBreakdown for tooltip detail (count per test category within the reason).
 * @param {object[]} rows
 * @param {Map<number, string>|null} [reasonLabelById]
 * @returns {object[]}
 */
export const buildOverdueReasonChartSeries = (rows = [], reasonLabelById = null) => (
  buildNestedCountChartSeries(
    rows,
    (row) => resolveReasonBucket(row, reasonLabelById),
    resolveCategoryBucket,
    "categoryBreakdown",
  )
);

/**
 * Resolve a display label for an SLA period bucket from API `label`.
 * Weekly labels may append "_<end>" — axis shows only the leading segment.
 */
const resolveSlaPeriodLabel = (period) => {
  if (period?.label == null || String(period.label).trim() === "") {
    return UNKNOWN_CHART_LABEL;
  }
  const rawLabel = String(period.label).trim();
  const underscoreIndex = rawLabel.indexOf("_");
  if (underscoreIndex > 0) {
    return rawLabel.slice(0, underscoreIndex);
  }
  return rawLabel;
};

/**
 * Parse overdue_sla `label` into period start/end for drill-down date_range.
 * Day/month: single label. Week: "start_end".
 * @param {*} label
 * @returns {{ periodStart: string, periodEnd: string }}
 */
const resolveSlaPeriodBounds = (label) => {
  const rawLabel = label != null ? String(label).trim() : "";
  if (!rawLabel) {
    return { periodStart: "", periodEnd: "" };
  }
  const underscoreIndex = rawLabel.indexOf("_");
  if (underscoreIndex > 0) {
    return {
      periodStart: rawLabel.slice(0, underscoreIndex),
      periodEnd: rawLabel.slice(underscoreIndex + 1),
    };
  }
  return { periodStart: rawLabel, periodEnd: rawLabel };
};

/**
 * Pivot overdue_sla series (one API result set per line) into Recharts multi-line data.
 * Each series entry is { seriesKey, legendName, periods: [{ label, sla }] }.
 * Values are percentages (sla ratio × 100). Empty / missing sla → 0.
 *
 * @param {Array<{
 *   seriesKey: string,
 *   legendName: string,
 *   periods?: Array<{ label?: string, sla?: number }>
 * }>} [seriesList]
 * @returns {{
 *   chartRows: object[],
 *   lineDescriptors: Array<{ dataKey: string, legendName: string, categoryIndex: number }>
 * }}
 */
export const buildOverdueSlaChartSeries = (seriesList = []) => {
  const safeSeriesList = Array.isArray(seriesList) ? seriesList : [];
  const labelKeys = new Set();

  safeSeriesList.forEach((series) => {
    const periods = Array.isArray(series?.periods) ? series.periods : [];
    periods.forEach((period) => {
      if (period?.label == null || String(period.label).trim() === "") {
        return;
      }
      labelKeys.add(String(period.label).trim());
    });
  });

  const sortedLabels = Array.from(labelKeys).sort((left, right) => left.localeCompare(right));

  const lineDescriptors = safeSeriesList.map((series, seriesIndex) => ({
    dataKey: series.seriesKey,
    legendName: series.legendName || series.seriesKey,
    categoryIndex: series.seriesKey === SLA_OVERALL_DATA_KEY ? -1 : seriesIndex,
  }));

  const slaBySeriesAndLabel = new Map();
  safeSeriesList.forEach((series) => {
    const periods = Array.isArray(series?.periods) ? series.periods : [];
    const valueByLabel = new Map();
    periods.forEach((period) => {
      if (period?.label == null || String(period.label).trim() === "") {
        return;
      }
      valueByLabel.set(String(period.label).trim(), toSlaPercentage(period.sla));
    });
    slaBySeriesAndLabel.set(series.seriesKey, valueByLabel);
  });

  const chartRows = sortedLabels.map((label) => {
    const { periodStart, periodEnd } = resolveSlaPeriodBounds(label);
    const row = {
      periodLabel: resolveSlaPeriodLabel({ label }),
      periodStart,
      periodEnd,
    };

    lineDescriptors.forEach((descriptor) => {
      const valueByLabel = slaBySeriesAndLabel.get(descriptor.dataKey);
      row[descriptor.dataKey] = valueByLabel?.get(label) ?? 0;
    });

    return row;
  });

  return {
    chartRows,
    lineDescriptors,
  };
};

/* -------------------------------------------------------------------------- */
/* Cell renderers                                                             */
/* -------------------------------------------------------------------------- */

const renderEllipsisWithTooltip = (displayValue) => {
  if (displayValue === EMPTY_CELL_VALUE) {
    return displayValue;
  }

  const tooltipTitle = String(displayValue);

  return (
    <Tooltip placement="topLeft" title={tooltipTitle}>
      <span className="DataOpsAnalytics__EllipsisCell">{displayValue}</span>
    </Tooltip>
  );
};

/**
 * Ant Design Table cell render — signature is (value, record, index).
 * Shows tooltip for truncated text.
 */
export const renderOverdueEllipsisCell = (value) => (
  renderEllipsisWithTooltip(formatOverdueCellValue(value))
);

/**
 * Phase column render — maps phase key to label, then tooltip.
 */
export const renderOverduePhaseEllipsisCell = (phaseKey) => (
  renderEllipsisWithTooltip(
    OVERDUE_PHASE_LABELS[phaseKey] || formatOverdueCellValue(phaseKey),
  )
);

/* -------------------------------------------------------------------------- */
/* Excel export                                                               */
/* -------------------------------------------------------------------------- */

const OVERDUE_EXPORT_COLUMNS = [
  { key: "customer_company", header: "Customer" },
  { key: "customer_id", header: "Customer ID" },
  { key: "workorder_id", header: "WO" },
  { key: "genista_id", header: "GBID" },
  { key: "category_name", header: "Test Category" },
  { key: "test_name", header: "Test" },
  { key: "phase", header: "Phase" },
  { key: "reason", header: "Overdue Reason" },
  { key: "result_status", header: "Result Status" },
  { key: "report_status", header: "Report Status" },
  { key: "received_date", header: "Received Date" },
  { key: "due_date", header: "Due Date" },
  { key: "days_overdue", header: "Days Overdue" },
  { key: "is_retest", header: "Is Retest" },
  { key: "has_rca", header: "Has RCA" },
  { key: "rca_created_date", header: "RCA Initiated" },
];

const formatExportBoolean = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
};

const formatExportRcaDate = (value) => {
  if (value == null || value === "") {
    return "";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const buildOverdueExportRow = (row) => ({
  customer_company: row.customer_company,
  customer_id: row.customer_id,
  workorder_id: row.workorder_id,
  genista_id: row.genista_id,
  category_name: row.category_name,
  test_name: row.test_name,
  phase: OVERDUE_PHASE_LABELS[row.phase] || row.phase,
  reason: row.reason,
  result_status: formatResultStatusLabel(row.result_status),
  report_status: formatReportStatusLabel(row.report_status),
  received_date: row.received_date,
  due_date: row.due_date,
  days_overdue: row.days_overdue,
  is_retest: formatExportBoolean(row.is_retest),
  has_rca: formatHasRcaExportValue(row.has_rca),
  rca_created_date: formatExportRcaDate(row.rca_created_date),
});

export const buildOverdueExportRows = (apiRows) => (
  Array.isArray(apiRows) ? apiRows.map(buildOverdueExportRow) : []
);

export const buildOverdueExportFileName = (scopeLabel = "") => (
  buildDataOpsExportFileName("Overdue", scopeLabel)
);

export const downloadOverdueExportExcel = (rows, fileName) => {
  downloadDataOpsExcel({
    rows,
    columns: OVERDUE_EXPORT_COLUMNS,
    fileName,
    sheetName: "Overdue",
  });
};
